import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Paperclip, X, CheckCircle2, Loader2 } from "lucide-react";
import { authFetch } from "@/lib/authFetch";
import { useToast } from "@/hooks/use-toast";

const TYPES = [
  { value: "suggestion", label: "Suggestion" },
  { value: "bug", label: "Bug" },
  { value: "idee", label: "Idée" },
] as const;

type FeedbackType = typeof TYPES[number]["value"];

interface SuggestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
}

export function SuggestionModal({ isOpen, onClose, userName }: SuggestionModalProps) {
  const [type, setType] = useState<FeedbackType>("suggestion");
  const [message, setMessage] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const handleClose = () => {
    if (isSubmitting) return;
    setMessage("");
    setFile(null);
    setIsDone(false);
    setType("suggestion");
    onClose();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "image/gif", "application/pdf"];
    if (!allowed.includes(f.type)) {
      toast({ title: "Format non supporté", description: "Images (JPG, PNG, WebP) ou PDF uniquement.", variant: "destructive" });
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast({ title: "Fichier trop lourd", description: "Taille maximale : 50 Mo.", variant: "destructive" });
      return;
    }
    setFile(f);
  };

  const handleSubmit = async () => {
    if (!message.trim()) {
      toast({ title: "Message obligatoire", description: "Veuillez saisir un message.", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);

    try {
      const formData = new FormData();
      formData.append("type", type);
      formData.append("message", message.trim());
      formData.append("contexte_url", window.location.pathname);
      if (file) formData.append("fichier", file);

      const res = await authFetch("/api/feedback", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Erreur lors de l'envoi.");
      }

      setIsDone(true);
      setTimeout(() => handleClose(), 2000);
    } catch (e: any) {
      toast({ title: "Erreur", description: e?.message || "Une erreur est survenue.", variant: "destructive" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) handleClose(); }}>
      <DialogContent className="max-w-md" aria-describedby="suggestion-desc">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            💡 Faire une suggestion
          </DialogTitle>
        </DialogHeader>

        {isDone ? (
          <div className="flex flex-col items-center gap-3 py-8 text-center">
            <CheckCircle2 className="w-12 h-12 text-green-500" />
            <p className="font-semibold text-foreground">Merci !</p>
            <p className="text-sm text-muted-foreground">Votre message a bien été envoyé.</p>
          </div>
        ) : (
          <div className="space-y-4">
            <p id="suggestion-desc" className="text-sm text-muted-foreground leading-relaxed">
              Votre avis est précieux ! Partagez vos remarques, signalez un problème ou donnez-nous des idées.
            </p>

            {/* Sélecteur de type */}
            <div className="space-y-1.5">
              <Label>Type</Label>
              <div className="flex gap-2" data-testid="feedback-type-selector">
                {TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    disabled={isSubmitting}
                    data-testid={`btn-type-${t.value}`}
                    className={`flex-1 py-1.5 px-3 rounded-lg text-sm font-medium border transition-colors ${
                      type === t.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-muted-foreground border-border hover:border-primary/50 hover:text-foreground"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="suggestion-name">Nom</Label>
              <Input
                id="suggestion-name"
                value={userName}
                disabled
                className="bg-muted text-muted-foreground"
                data-testid="input-suggestion-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="suggestion-message">Votre message <span className="text-destructive">*</span></Label>
              <Textarea
                id="suggestion-message"
                placeholder="Décrivez votre suggestion, problème ou idée..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={5}
                disabled={isSubmitting}
                className="resize-none"
                data-testid="textarea-suggestion-message"
              />
            </div>

            <div className="space-y-1.5">
              <Label>Pièce jointe <span className="text-xs text-muted-foreground">(optionnel)</span></Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf"
                className="hidden"
                onChange={handleFileChange}
                data-testid="input-suggestion-file"
              />
              {file ? (
                <div className="flex items-center gap-2 p-2.5 rounded-lg border bg-secondary/50 text-sm">
                  <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate text-foreground">{file.name}</span>
                  <button
                    onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                    className="text-muted-foreground hover:text-destructive transition-colors"
                    data-testid="button-remove-attachment"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  className="gap-2"
                  data-testid="button-add-attachment"
                >
                  <Paperclip className="w-4 h-4" />
                  Joindre un fichier
                </Button>
              )}
            </div>

            <div className="flex gap-2 justify-end pt-1">
              <Button variant="outline" onClick={handleClose} disabled={isSubmitting} data-testid="button-suggestion-cancel">
                Annuler
              </Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !message.trim()} data-testid="button-suggestion-send">
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-2 mr-2 animate-spin" />
                    Envoi…
                  </>
                ) : "Envoyer"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
